import { useState, useMemo, useEffect, FC, useRef } from 'react';
import { Link as ChakraLink } from '@chakra-ui/react';
import { guardarExpediente } from '../config/index';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Input,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  Checkbox,
  Textarea,
  Text,
  Button,
  VStack,
  Stack,
  useColorModeValue,
  IconButton,
  Flex,
  Heading,
  Image,
  HStack,
  Circle,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';

interface FormData {
  // Tipo de denuncia
  tipoBarrera: string;
  // Paso 1: Información Personal
  nombreApellidos: string;
  direccion: string;
  codigoPostal: string;
  localidad: string;
  provincia: string;
  telefonoFijo: string;
  movil: string;
  email: string;
  anoNacimiento: string;
  genero: string;
  // Paso 2: Información de Discapacidad
  tipoPersona: 'no_tiene_discapacidad' | 'tiene_discapacidad' | '';
  tipoDiscapacidad: string;
  porcentajeDiscapacidad: string;
  baremoMovilidad: string;
  // Paso 3: Descripción y Consentimiento
  descripcion: string;
  fotografia?: File;
  aceptaPolitica: boolean;
}

interface LocationState {
  tipoBarrera?: string;
}

export const UserForm: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tipoBarrera } = location.state as LocationState || {};
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const totalSteps = 3;
  const brandRed = useColorModeValue('red.500', 'red.300');
  const errorColor = useColorModeValue('red.500', 'red.300');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!tipoBarrera) {
      navigate('/');
    }
  }, [tipoBarrera, navigate]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  }, []);

  const provincias = [
    "Álava", "Albacete", "Alicante", "Almería", "Asturias", "Ávila", "Badajoz", "Barcelona",
    "Burgos", "Cáceres", "Cádiz", "Cantabria", "Castellón", "Ciudad Real", "Córdoba", "Cuenca",
    "Gerona", "Granada", "Guadalajara", "Guipúzcoa", "Huelva", "Huesca", "Islas Baleares",
    "Jaén", "La Coruña", "La Rioja", "Las Palmas", "León", "Lérida", "Lugo", "Madrid", "Málaga",
    "Murcia", "Navarra", "Orense", "Palencia", "Pontevedra", "Salamanca", "Santa Cruz de Tenerife",
    "Segovia", "Sevilla", "Soria", "Tarragona", "Teruel", "Toledo", "Valencia", "Valladolid",
    "Vizcaya", "Zamora", "Zaragoza"
  ];

  const initialFormData: FormData = {
    tipoBarrera: tipoBarrera || '',
    nombreApellidos: '',
    direccion: '',
    codigoPostal: '',
    localidad: '',
    provincia: '',
    telefonoFijo: '',
    movil: '',
    email: '',
    anoNacimiento: '',
    genero: '',
    tipoPersona: '',
    tipoDiscapacidad: '',
    porcentajeDiscapacidad: '',
    baremoMovilidad: '',
    descripcion: '',
    aceptaPolitica: false
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Archivo demasiado grande',
          description: 'El tamaño máximo permitido es de 5 MB',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        fotografia: file
      }));

      // Preview local (object URL); la persistencia va a Convex Storage al guardar.
      setPreviewUrl((prev) => {
        if (prev.startsWith("blob:")) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    }
  };

  const handleRadioChange = (name: string) => (nextValue: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validateStep = (step: number) => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    switch (step) {
      case 1:
        if (!formData.nombreApellidos) newErrors.nombreApellidos = 'Este campo es necesario';
        if (!formData.direccion) newErrors.direccion = 'Este campo es necesario';
        if (!formData.codigoPostal) newErrors.codigoPostal = 'Este campo es necesario';
        if (formData.codigoPostal && !/^\d{5}$/.test(formData.codigoPostal)) {
          newErrors.codigoPostal = 'Debe tener 5 dígitos';
        }
        if (!formData.localidad) newErrors.localidad = 'Este campo es necesario';
        if (!formData.provincia) newErrors.provincia = 'Este campo es necesario';
        if (!formData.movil) newErrors.movil = 'Este campo es necesario';
        if (formData.movil && !/^\d{9}$/.test(formData.movil)) {
          newErrors.movil = 'Debe tener 9 dígitos';
        }
        if (formData.telefonoFijo && !/^\d{9}$/.test(formData.telefonoFijo)) {
          newErrors.telefonoFijo = 'Debe tener 9 dígitos';
        }
        if (!formData.email) newErrors.email = 'Este campo es necesario';
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Email no válido';
        }
        if (!formData.anoNacimiento) newErrors.anoNacimiento = 'Este campo es necesario';
        if (!formData.genero) newErrors.genero = 'Este campo es necesario';
        break;
      case 2:
        if (!formData.tipoPersona) newErrors.tipoPersona = 'Este campo es necesario';
        if (formData.tipoPersona === 'tiene_discapacidad') {
          if (!formData.tipoDiscapacidad) newErrors.tipoDiscapacidad = 'Este campo es necesario';
          if (formData.porcentajeDiscapacidad && 
              (Number(formData.porcentajeDiscapacidad) < 0 || Number(formData.porcentajeDiscapacidad) > 100)) {
            newErrors.porcentajeDiscapacidad = 'Debe estar entre 0 y 100';
          }
          if (!formData.baremoMovilidad) newErrors.baremoMovilidad = 'Este campo es necesario';
        }
        break;
      case 3:
        if (!formData.descripcion) newErrors.descripcion = 'Este campo es necesario';
        if (!formData.aceptaPolitica) newErrors.aceptaPolitica = 'Debes aceptar la política de protección de datos';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentStep === totalSteps && validateStep(currentStep)) {
      // Mostrar diálogo de confirmación
      onOpen();
    } else if (validateStep(currentStep)) {
      handleNext();
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      // Cerrar el diálogo
      onClose();
      
      // Preferir el File original → Convex Storage (no persistir base64).
      const expedienteData = {
        tipoBarrera: tipoBarrera || '',
        email: formData.email,
        nombreApellidos: formData.nombreApellidos,
        descripcion: formData.descripcion,
        Departamento: 'Administrativo' as 'Administrativo' | 'Arquitecto' | 'Jurídico',
        comunicacionesInternas: [],
        // Información Personal
        direccion: formData.direccion,
        codigoPostal: formData.codigoPostal,
        localidad: formData.localidad,
        provincia: formData.provincia,
        telefonoFijo: formData.telefonoFijo,
        movil: formData.movil,
        fechaNacimiento: formData.anoNacimiento,
        genero: formData.genero,
        // Información de Discapacidad
        tipoPersona: formData.tipoPersona,
        tipoDiscapacidad: formData.tipoDiscapacidad,
        porcentajeDiscapacidad: formData.porcentajeDiscapacidad,
        gradoDiscapacidad: formData.porcentajeDiscapacidad ? `${formData.porcentajeDiscapacidad}%` : '',
        baremoMovilidad: formData.baremoMovilidad,
        imagenUrl: '',
        fotografiaFile: formData.fotografia,
      };
      const expedienteId = await guardarExpediente(expedienteData);
      
      // Navegar a la página de resumen
      navigate('/submission-summary', {
        state: {
          expediente: expedienteId,
          tipoBarrera: tipoBarrera,
          nombreApellidos: formData.nombreApellidos,
          localidad: formData.localidad,
          provincia: formData.provincia,
          email: formData.email
        }
      });
    } catch (error) {
      console.error('Error al guardar expediente:', error);
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1:
        return "Información Personal";
      case 2:
        return "Información de Discapacidad";
      case 3:
        return "Descripción y Consentimiento";
      default:
        return "";
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Nombre y apellidos</FormLabel>
              <Input
                name="nombreApellidos"
                value={formData.nombreApellidos}
                onChange={handleInputChange}
                isInvalid={!!errors.nombreApellidos}
              />
              {errors.nombreApellidos && (
                <Text color={errorColor} fontSize="sm" mt={1}>
                  {errors.nombreApellidos}
                </Text>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Dirección donde reside</FormLabel>
              <Input
                name="direccion"
                value={formData.direccion}
                onChange={handleInputChange}
                isInvalid={!!errors.direccion}
              />
              {errors.direccion && (
                <Text color={errorColor} fontSize="sm" mt={1}>
                  {errors.direccion}
                </Text>
              )}
            </FormControl>

            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Código Postal</FormLabel>
                <Input
                  name="codigoPostal"
                  type="text"
                  maxLength={5}
                  pattern="[0-9]{5}"
                  value={formData.codigoPostal}
                  onChange={handleInputChange}
                  placeholder="28001"
                  isInvalid={!!errors.codigoPostal}
                />
                {errors.codigoPostal && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.codigoPostal}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Localidad</FormLabel>
                <Input
                  name="localidad"
                  value={formData.localidad}
                  onChange={handleInputChange}
                  isInvalid={!!errors.localidad}
                />
                {errors.localidad && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.localidad}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Provincia</FormLabel>
                <Select
                  name="provincia"
                  value={formData.provincia}
                  onChange={handleSelectChange}
                  placeholder="Selecciona provincia"
                  isInvalid={!!errors.provincia}
                >
                  {provincias.map((provincia) => (
                    <option key={provincia} value={provincia}>
                      {provincia}
                    </option>
                  ))}
                </Select>
                {errors.provincia && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.provincia}
                  </Text>
                )}
              </FormControl>
            </Stack>

            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <FormControl>
                <FormLabel>Teléfono fijo</FormLabel>
                <Input
                  name="telefonoFijo"
                  type="tel"
                  pattern="[0-9]{9}"
                  maxLength={9}
                  value={formData.telefonoFijo}
                  onChange={handleInputChange}
                  placeholder="912345678"
                  isInvalid={!!errors.telefonoFijo}
                />
                {errors.telefonoFijo && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.telefonoFijo}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Móvil</FormLabel>
                <Input
                  name="movil"
                  type="tel"
                  pattern="[0-9]{9}"
                  maxLength={9}
                  value={formData.movil}
                  onChange={handleInputChange}
                  placeholder="612345678"
                  isInvalid={!!errors.movil}
                />
                {errors.movil && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.movil}
                  </Text>
                )}
              </FormControl>
            </Stack>

            <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Correo Electrónico</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  isInvalid={!!errors.email}
                />
                {errors.email && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.email}
                  </Text>
                )}
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Año de Nacimiento</FormLabel>
                <Select
                  name="anoNacimiento"
                  value={formData.anoNacimiento}
                  onChange={handleSelectChange}
                  placeholder="Selecciona año"
                  isInvalid={!!errors.anoNacimiento}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
                {errors.anoNacimiento && (
                  <Text color={errorColor} fontSize="sm" mt={1}>
                    {errors.anoNacimiento}
                  </Text>
                )}
              </FormControl>
            </Stack>

            <FormControl isRequired>
              <FormLabel>Género</FormLabel>
              <RadioGroup
                name="genero"
                value={formData.genero}
                onChange={handleRadioChange("genero")}
              >
                <Stack direction="row">
                  <Radio value="masculino">Masculino</Radio>
                  <Radio value="femenino">Femenino</Radio>
                  <Radio value="otros">Otros</Radio>
                </Stack>
              </RadioGroup>
              {errors.genero && (
                <Text color={errorColor} fontSize="sm" mt={1}>
                  {errors.genero}
                </Text>
              )}
            </FormControl>
          </VStack>
        );
      case 2:
        return (
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="lg" mb={4}>¿Sufres alguna discapacidad?</FormLabel>
              <RadioGroup
                name="tipoPersona"
                value={formData.tipoPersona}
                onChange={handleRadioChange("tipoPersona")}
              >
                <Stack direction={{ base: 'column', md: 'row' }} spacing={8}>
                  <Box
                    p={6}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={formData.tipoPersona === 'no_tiene_discapacidad' ? 'red.500' : 'gray.200'}
                    cursor="pointer"
                    onClick={() => handleRadioChange("tipoPersona")("no_tiene_discapacidad")}
                    _hover={{ borderColor: 'red.300' }}
                    transition="all 0.2s"
                    flex="1"
                  >
                    <VStack spacing={4}>
                      <Circle size="50px" bg={formData.tipoPersona === 'no_tiene_discapacidad' ? 'red.500' : 'gray.100'}>
                        <CloseIcon color={formData.tipoPersona === 'no_tiene_discapacidad' ? 'white' : 'gray.500'} />
                      </Circle>
                      <Radio value="no_tiene_discapacidad" size="lg">
                        <Text fontSize="md" fontWeight="bold">No tengo discapacidad</Text>
                      </Radio>
                    </VStack>
                  </Box>
                  <Box
                    p={6}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={formData.tipoPersona === 'tiene_discapacidad' ? 'red.500' : 'gray.200'}
                    cursor="pointer"
                    onClick={() => handleRadioChange("tipoPersona")("tiene_discapacidad")}
                    _hover={{ borderColor: 'red.300' }}
                    transition="all 0.2s"
                    flex="1"
                  >
                    <VStack spacing={4}>
                      <Circle size="50px" bg={formData.tipoPersona === 'tiene_discapacidad' ? 'red.500' : 'gray.100'}>
                        <CheckIcon color={formData.tipoPersona === 'tiene_discapacidad' ? 'white' : 'gray.500'} />
                      </Circle>
                      <Radio value="tiene_discapacidad" size="lg">
                        <Text fontSize="md" fontWeight="bold">Sí, tengo discapacidad</Text>
                      </Radio>
                    </VStack>
                  </Box>
                </Stack>
              </RadioGroup>
              {errors.tipoPersona && (
                <Text color={errorColor} fontSize="sm" mt={1}>
                  {errors.tipoPersona}
                </Text>
              )}
            </FormControl>

            {formData.tipoPersona === 'tiene_discapacidad' && (
              <Box
                mt={6}
                p={6}
                borderWidth="1px"
                borderRadius="lg"
                borderColor="gray.200"
                bg="gray.50"
              >
                <VStack spacing={6} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Tipo de Discapacidad</FormLabel>
                    <Select
                      name="tipoDiscapacidad"
                      value={formData.tipoDiscapacidad}
                      onChange={handleSelectChange}
                      isInvalid={!!errors.tipoDiscapacidad}
                      bg="white"
                      size="lg"
                    >
                      <option value="">Selecciona un tipo</option>
                      <option value="fisica">Física u orgánica</option>
                      <option value="intelectual">Intelectual</option>
                      <option value="mental">Mental</option>
                      <option value="sensorial">Sensorial</option>
                    </Select>
                    {errors.tipoDiscapacidad && (
                      <Text color={errorColor} fontSize="sm" mt={1}>
                        {errors.tipoDiscapacidad}
                      </Text>
                    )}
                  </FormControl>

                  <FormControl>
                    <FormLabel>Porcentaje de discapacidad</FormLabel>
                    <Input
                      name="porcentajeDiscapacidad"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.porcentajeDiscapacidad}
                      onChange={handleInputChange}
                      placeholder="Ej: 33"
                      isInvalid={!!errors.porcentajeDiscapacidad}
                      bg="white"
                      size="lg"
                    />
                    {errors.porcentajeDiscapacidad && (
                      <Text color={errorColor} fontSize="sm" mt={1}>
                        {errors.porcentajeDiscapacidad}
                      </Text>
                    )}
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>¿Tienes baremo de movilidad reducida?</FormLabel>
                    <RadioGroup
                      name="baremoMovilidad"
                      value={formData.baremoMovilidad}
                      onChange={handleRadioChange("baremoMovilidad")}
                    >
                      <Stack direction="row" spacing={8}>
                        <Radio value="si" size="lg">Sí</Radio>
                        <Radio value="no" size="lg">No</Radio>
                      </Stack>
                    </RadioGroup>
                    {errors.baremoMovilidad && (
                      <Text color={errorColor} fontSize="sm" mt={1}>
                        {errors.baremoMovilidad}
                      </Text>
                    )}
                  </FormControl>
                </VStack>
              </Box>
            )}
          </VStack>
        );
      case 3:
        return (
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Descripción Detallada</FormLabel>
              <Box position="relative">
                <Textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  minH="100px"
                  maxLength={2000}
                  isInvalid={!!errors.descripcion}
                />
                <Text
                  position="absolute"
                  right="0"
                  bottom="-20px"
                  fontSize="sm"
                  color="gray.500"
                >
                  {formData.descripcion.length}/2000 caracteres
                </Text>
              </Box>
              {errors.descripcion && (
                <Text color={errorColor} fontSize="sm" mt={1}>
                  {errors.descripcion}
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Fotografía de la barrera</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                sx={{
                  '::file-selector-button': {
                    border: 'none',
                    outline: 'none',
                    mr: 2,
                    bg: 'red.500',
                    color: 'white',
                    borderRadius: 'md',
                    px: 4,
                    py: 2,
                    cursor: 'pointer',
                    _hover: {
                      bg: 'red.600'
                    }
                  }
                }}
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                Puedes adjuntar una fotografía de la barrera (opcional)
              </Text>
              {previewUrl && (
                <Box mt={2}>
                  <Image
                    src={previewUrl}
                    alt="Vista previa"
                    maxH="200px"
                    objectFit="contain"
                    borderRadius="md"
                  />
                </Box>
              )}
            </FormControl>

            <FormControl isRequired>
              <Stack direction="row" align="center">
                <Checkbox
                  name="aceptaPolitica"
                  isChecked={formData.aceptaPolitica}
                  onChange={handleCheckboxChange}
                />
                <Text fontSize="sm">
                  Acepto la <ChakraLink href="https://famma.org/aviso-legal/" color="brand.500" isExternal>política de protección de datos</ChakraLink>
                </Text>
              </Stack>
              {errors.aceptaPolitica && (
                <Text color={errorColor} fontSize="sm" mt={1}>
                  {errors.aceptaPolitica}
                </Text>
              )}
            </FormControl>
          </VStack>
        );
      default:
        return null;
    }
  };

  return (
    <Box 
      minH="100vh"
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
      py={6}
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Box as="form" onSubmit={handleSubmit} maxW="800px" mx="auto" px={6} position="relative" zIndex={1}>
      <VStack spacing={8} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                as="h1"
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                {tipoBarrera}
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>
        <Text 
          color="gray.700"
          textAlign="center"
          fontWeight="semibold"
          bg="whiteAlpha.600"
          py={2}
          px={4}
          borderRadius="lg"
          backdropFilter="blur(10px)"
        >
          {tipoBarrera === "Ayudas Técnicas y Consultas" 
            ? "Completa el formulario para solicitar información técnica"
            : "Completa el formulario para reportar una barrera"
          }
        </Text>

        <Box 
          bg="white" 
          p={6} 
          borderRadius="lg" 
          boxShadow="md"
          border="1px"
          borderColor="gray.200"
        >
          <HStack justify="center" spacing={8} mb={8}>
            {[1, 2, 3].map((step) => (
              <VStack key={step}>
                <Circle
                  size="40px"
                  bg={currentStep >= step ? 'red.500' : 'gray.200'}
                  color="white"
                  fontWeight="bold"
                >
                  {step}
                </Circle>
                <Text fontSize="sm" color={currentStep >= step ? 'red.500' : 'gray.500'}>
                  {getStepTitle(step)}
                </Text>
              </VStack>
            ))}
          </HStack>

          <Text fontSize="xl" fontWeight="bold" mb={6} color={brandRed}>
            Paso {currentStep}: {getStepTitle(currentStep)}
          </Text>

          {renderStepContent()}

          <Flex justify="space-between" mt={8}>
            <IconButton
              icon={<ChevronLeftIcon />}
              onClick={handlePrev}
              isDisabled={currentStep === 1}
              aria-label="Anterior"
              colorScheme="red"
              variant="outline"
            />
            
            <Button
              rightIcon={currentStep === totalSteps ? undefined : <ChevronRightIcon />}
              type="submit"
              colorScheme="red"
              size="lg"
              isDisabled={currentStep === totalSteps && !formData.aceptaPolitica}
            >
              {currentStep === totalSteps ? 'Enviar' : 'Siguiente'}
            </Button>
          </Flex>
        </Box>
      </VStack>
      </Box>

      {/* Diálogo de confirmación */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar envío de denuncia
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Está seguro de que desea enviar la denuncia? Por favor, confirme que ha revisado toda la información y la documentación necesaria antes de proceder.
              <br /><br />
              Una vez enviada, la denuncia será procesada por nuestro equipo.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                No, revisar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmSubmit} ml={3}>
                Sí, enviar denuncia
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};
